'use client'

import { createContext, useCallback, PropsWithChildren, useContext, useRef } from 'react'

export type TriggerLoginOptions = {
  onActionClick?: () => void
  onAuthSuccess?: () => void
  prefillEmail?: string
  referrerEmail?: string
}
export type TriggerLoginFn = (options?: TriggerLoginOptions) => void

type AuthButtonBridgeValue = {
  triggerLogin: TriggerLoginFn
  registerRealTrigger: (fn: TriggerLoginFn) => void
}

const AuthButtonBridgeContext = createContext<AuthButtonBridgeValue | null>(null)

export function AuthButtonBridgeProvider({ children }: PropsWithChildren) {
  const realTriggerRef = useRef<TriggerLoginFn | null>(null)
  const pendingRef = useRef<Parameters<TriggerLoginFn>[0] | null>(null)

  const triggerLogin = useCallback<TriggerLoginFn>((opts) => {
    const real = realTriggerRef.current
    if (real) {
      real(opts)
      return
    }
    pendingRef.current = opts ?? null
  }, [])

  const registerRealTrigger = useCallback((fn: TriggerLoginFn) => {
    realTriggerRef.current = fn
    const pending = pendingRef.current
    if (pending !== null) {
      pendingRef.current = null
      fn(pending)
    }
  }, [])

  const value: AuthButtonBridgeValue = {
    triggerLogin,
    registerRealTrigger,
  }

  return (
    <AuthButtonBridgeContext.Provider value={value}>
      {children}
    </AuthButtonBridgeContext.Provider>
  )
}

export function useAuthButtonBridge(): AuthButtonBridgeValue | null {
  return useContext(AuthButtonBridgeContext)
}
